import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowDown, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { ProductShowcase, products } from "../sections/products";

const fade = {
  hidden: { opacity: 0, y: 22 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
};

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.07, delayChildren: 0.05 },
  },
};

export default function Products() {
  const reduceMotion = useReducedMotion();

  return (
    <>
      <Helmet>
        <title>Products | Velozent — AI-Native Enterprise Platforms</title>
        <meta
          name="description"
          content="Explore Velozent's AI-native enterprise platforms — Accuro 360 (identity governance), VeloDesk (healthcare ITSM), MagixDB (natural-language analytics), Recroid (AI recruitment), and VeloCapture (lead generation & government bid intelligence)."
        />
        <meta
          name="keywords"
          content="Velozent products, Accuro 360, VeloDesk, MagixDB, Recroid, VeloCapture, AI enterprise software, identity governance, healthcare ITSM, natural language SQL, AI recruitment, AI lead generation, government bid intelligence"
        />
        <meta
          property="og:title"
          content="Products | Velozent — AI-Native Enterprise Platforms"
        />
        <meta
          property="og:description"
          content="AI-native enterprise platforms for identity governance, healthcare ITSM, natural-language analytics, and recruitment."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://velozent.com/products" />
        <link rel="canonical" href="https://velozent.com/products" />
      </Helmet>

      <section className="relative overflow-hidden bg-shark-950 px-4 pb-16 pt-20 text-white sm:px-6 sm:pb-20 sm:pt-24 lg:pb-28 lg:pt-32">
        <div className="hero-mesh">
          <div className="hero-mesh-orb hero-mesh-orb--1" />
          <div className="hero-mesh-orb hero-mesh-orb--2" />
          <div className="hero-mesh-orb hero-mesh-orb--3" />
          <div className="hero-mesh-grain" />
        </div>

        <div
          className="pointer-events-none absolute inset-x-0 top-0 -z-0 h-[420px] opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.7) 1px, transparent 0)",
            backgroundSize: "32px 32px",
            maskImage:
              "linear-gradient(180deg, #000 0%, #000 40%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(180deg, #000 0%, #000 40%, transparent 100%)",
          }}
          aria-hidden
        />

        <div className="relative z-10 mx-auto max-w-5xl">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="visible"
            className="text-center"
          >
            <motion.div
              variants={fade}
              className="mx-auto inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 backdrop-blur"
            >
              <Sparkles className="h-3 w-3 text-burnt-sienna-400" />
              <span className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-white/80">
                The Velozent product suite
              </span>
            </motion.div>

            <motion.h1
              variants={fade}
              className="mx-auto mt-6 max-w-4xl text-balance text-[clamp(2rem,5.4vw,3.75rem)] font-extrabold leading-[1.06] tracking-tight text-white sm:mt-7 sm:leading-[1.04]"
            >
              AI-Native{" "}
              <span className="bg-gradient-to-r from-burnt-sienna-300 via-burnt-sienna-400 to-burnt-sienna-500 bg-clip-text text-transparent">
                Enterprise Platforms
              </span>
            </motion.h1>

            <motion.p
              variants={fade}
              className="mx-auto mt-5 max-w-3xl text-[0.98rem] leading-relaxed text-white/[0.82] sm:mt-6 sm:text-[1.05rem] md:text-[1.1rem]"
            >
              Intelligent platforms designed to automate operations, accelerate
              decision-making, and modernize enterprise workflows.
            </motion.p>

            <motion.div
              variants={fade}
              className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-3 sm:mt-10"
            >
              <Button
                asChild
                size="lg"
                className="group h-11 border-0 px-6 text-[13.5px] font-bold text-white shadow-[0_8px_36px_-8px_rgba(231,109,56,0.75)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_44px_-8px_rgba(231,109,56,0.55)] sm:h-12 sm:px-8 sm:text-[14px]"
                style={{ backgroundColor: "#e76d38" }}
              >
                <Link to="/contact">
                  Request a Demo
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </Button>
              <a
                href={`#${products[0].id}`}
                className="group inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.16em] text-white/75 transition-colors hover:text-white"
              >
                Explore the platforms
                <ArrowDown className="h-3.5 w-3.5 transition-transform group-hover:translate-y-0.5" />
              </a>
            </motion.div>

            <motion.div
              variants={fade}
              className="mt-10 flex flex-wrap items-center justify-center gap-x-2 gap-y-2.5 sm:mt-14"
            >
              {products.map((p, i) => (
                <a
                  key={p.id}
                  href={`#${p.id}`}
                  className="group inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.025] px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/72 transition-colors hover:border-white/20 hover:bg-white/[0.05] hover:text-white sm:gap-2 sm:px-3.5 sm:py-1.5 sm:text-[10.5px] sm:tracking-[0.18em]"
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full transition-shadow"
                    style={{
                      backgroundColor: p.accent.primary,
                      boxShadow: `0 0 0 0 ${p.accent.glow}`,
                    }}
                  />
                  <span className="tabular-nums text-white/50">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{p.name}</span>
                </a>
              ))}
            </motion.div>
          </motion.div>
        </div>

        <div
          className="pointer-events-none absolute bottom-0 left-0 right-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(231,109,56,0.6), transparent)",
          }}
          aria-hidden
        />
      </section>

      {products.map((product, i) => (
        <ProductShowcase
          key={product.id}
          product={product}
          index={i}
          total={products.length}
          reverse={i % 2 === 1}
        />
      ))}

      <section className="relative overflow-hidden border-t border-white/5 bg-[#070b13] px-4 py-14 text-center sm:px-6 sm:py-20 lg:py-28">
        <div
          className="pointer-events-none absolute inset-0 -z-0 bg-gradient-to-br from-[#0d1f28] via-[#070b13] to-[#0a0f18]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute left-1/2 top-0 -z-0 h-[420px] w-[820px] -translate-x-1/2 rounded-full blur-[120px]"
          style={{ backgroundColor: "rgba(231, 109, 56, 0.18)" }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -right-[15%] bottom-0 -z-0 h-[300px] w-[300px] rounded-full blur-[100px]"
          style={{ backgroundColor: "rgba(44, 110, 134, 0.25)" }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 -z-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.7) 1px, transparent 0)",
            backgroundSize: "32px 32px",
            maskImage:
              "radial-gradient(ellipse 70% 60% at 50% 30%, #000 0%, transparent 80%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 70% 60% at 50% 30%, #000 0%, transparent 80%)",
          }}
          aria-hidden
        />

        <motion.div
          className="relative mx-auto max-w-3xl"
          initial={reduceMotion ? false : { opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-burnt-sienna-400 sm:text-[11px]">
            Your next step
          </p>
          <h2 className="mt-3 text-[clamp(1.625rem,3.6vw,2.75rem)] font-extrabold leading-[1.1] tracking-tight text-white sm:mt-4 sm:leading-[1.08]">
            Built for Modern Enterprise{" "}
            <span className="bg-gradient-to-r from-burnt-sienna-300 via-burnt-sienna-400 to-burnt-sienna-500 bg-clip-text text-transparent">
              Operations
            </span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-[0.95rem] leading-relaxed text-white/[0.86] sm:mt-5 sm:text-[1.02rem]">
            Explore how Velozent&apos;s AI-native platforms help organizations
            automate workflows, improve operational intelligence, and scale with
            confidence.
          </p>

          <div className="mt-7 flex justify-center sm:mt-9">
            <Button
              asChild
              size="lg"
              className="group h-12 border-0 px-8 text-[14px] font-bold text-white shadow-[0_10px_40px_-10px_rgba(231,109,56,0.7)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_16px_48px_-10px_rgba(231,109,56,0.6)] sm:h-[54px] sm:px-10 sm:text-[15px]"
              style={{ backgroundColor: "#e76d38" }}
            >
              <Link to="/contact" state={{ intent: "demo" }}>
                Request Demo
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Button>
          </div>

          <p className="mt-5 text-[11.5px] font-medium text-white/55 sm:mt-6 sm:text-[12px]">
            No obligation. Just a conversation with our enterprise team.
          </p>
        </motion.div>
      </section>
    </>
  );
}
